import { useState } from 'react'
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, StoreDispatch } from '../../store/configure-store'
import { logout } from '../../store/user'
import { envService } from '../../services/env-service'

const IconCompany = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
        <path d="M9 22V12h6v10" />
        <path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01" />
    </svg>
)

const IconDatabase = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
)

const IconHealthReport = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
)

const IconCloud = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
    </svg>
)

const IconOnboarding = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="14" height="17" rx="2" ry="2" />
        <path d="M9 21V14h-2v7" />
        <path d="M6 8h.01M12 8h.01M6 11h.01M12 11h.01" />
        <line x1="20" y1="7" x2="20" y2="13" />
        <line x1="23" y1="10" x2="17" y2="10" />
    </svg>
)

const pageIcons: Record<string, React.FC> = {
    companies: IconCompany,
    database: IconDatabase,
    'health report': IconHealthReport,
    AWS: IconCloud,
    onboarding: IconOnboarding,
}

interface NavSideBarProps {
    logo?: string
}

export const NavSideBar: React.FC<NavSideBarProps> = ({ logo }) => {

    const navigate = useNavigate()
    const dispatch = useDispatch<StoreDispatch>()
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
    const [searchParams] = useSearchParams()
    const userEmail = useSelector((state: RootState) => state.users.user)

    const getUserDisplayName = () => {
        if (!userEmail) return ''
        const local = userEmail.split('@')[0]
        return local.split(/[._-]/).map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    }

    const getUserInitials = () => {
        const name = getUserDisplayName()
        const parts = name.split(' ')
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
        return name.substring(0, 2).toUpperCase()
    }

    const onLogout = () => {
        dispatch(logout())
        setIsProfileMenuOpen(false)
        navigate('/login')
    }

    const onToggleProfileMenu = () => {
        setIsProfileMenuOpen(isOpen => !isOpen)
    }

    const pages = ['companies', 'database', 'health report', 'AWS', 'onboarding']

    const isNonProd = !envService.isProd

    return <nav className={`nav-side-bar ${isNonProd ? 'env-non-prod' : ''}`}>
        {logo && <div className="sidebar-logo clickable" onClick={() => navigate('/companies/audit-controls')}>
            <img src={logo} alt="logo" />
            <span className="logo-subtitle">Enterprise</span>
            <span className="logo-title">Backoffice</span>
            <span className={`env-badge ${isNonProd ? 'env-non-prod' : ''}`}>{envService.label}</span>
        </div>}
        <section className="top-navigation-bar">
            <ul className="clean-list">
                {pages.map(name => {
                    const Icon = pageIcons[name]
                    const company = searchParams.get('company')
                    const path = name.replace(' ', '-')
                    const to = (name === 'companies' && company) ? `${path}?company=${company}` : path
                    const tooltip = name === 'onboarding' ? 'Company Onboarding' : name
                    return <li key={name} data-tooltip={tooltip}>
                        <NavLink to={to}>
                            <Icon />
                        </NavLink>
                    </li>
                })}
            </ul>
        </section>

        <section className="bottom-navigation-bar">
            <div className={`profile-card ${isProfileMenuOpen ? 'open' : ''}`} onClick={onToggleProfileMenu}>
                <div className="profile-avatar">{getUserInitials()}</div>
                <div className="profile-tooltip">
                    <span className="profile-name">{getUserDisplayName()}</span>
                    <span className="profile-role">Admin</span>
                    <span className="profile-email">{userEmail}</span>
                </div>
            </div>
            <div className={`profile-menu ${isProfileMenuOpen ? "open" : ""}`}>
                <ul className='clean-list'>
                    <li onClick={onLogout}>Logout</li>
                </ul>
            </div>
        </section>
    </nav>
}
